/**
 * Decodes HTML entities into their character equivalents.
 * Uses the browser's DOMParser when available, with a regex-based fallback for SSR.
 *
 * @param html - The HTML string to decode
 * @returns Decoded plain text
 */
export const decodeHtmlEntities = (html: string): string => {
  if (!html) return "";

  // 1. Try browser DOMParser for full safety
  if (typeof window !== "undefined") {
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");
      return doc.body.textContent || "";
    } catch (e) {
      // Fallback if DOMParser fails
    }
  }

  // 2. Comprehensive SSR fallback: most common entities
  return html
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    // Latin characters with accents (commonly found in Vietnamese)
    .replace(/&[a-zA-Z0-9#]+;/g, (match) => {
      const entities: Record<string, string> = {
        "&agrave;": "à", "&aacute;": "á", "&ả;": "ả", "&ã;": "ã", "&ạ;": "ạ",
        "&egrave;": "è", "&é;": "é", "&ẻ;": "ẻ", "&ẽ;": "ẽ", "&ẹ;": "ẹ",
        "&igrav;": "ì", "&iacute;": "í", "&ỉ;": "ỉ", "&ĩ;": "ĩ", "&ị;": "ị",
        "&ograv;": "ò", "&oacute;": "ó", "&ỏ;": "ỏ", "&õ;": "õ", "&ọ;": "ọ",
        "&ugrav;": "ù", "&uacute;": "ú", "&ủ;": "ủ", "&ũ;": "ũ", "&ụ;": "ụ",
        "&ygrav;": "ỳ", "&yacute;": "ý", "&ỷ;": "ỷ", "&ỹ;": "ỹ", "&ỵ;": "ỵ",
        "&Agrave;": "À", "&Aacute;": "Á", "&Egrave;": "È", "&Eacute;": "É",
        "&Igrav;": "Ì", "&Iacute;": "Í", "&Ograv;": "Ò", "&Oacute;": "Ó",
        "&Ugrav;": "Ù", "&Uacute;": "Ú", "&Ygrav;": "Ỳ", "&Yacute;": "Ý",
      };
      return entities[match] || match;
    });
};

/**
 * Strips HTML tags from a string.
 *
 * @param html - The HTML string to strip
 * @returns Plain text without tags
 */
export const stripHtml = (html: string): string => {
  if (!html) return "";
  return html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
};

/**
 * Safely formats an excerpt from HTML content.
 * Decodes entities first to ensure truncation doesn't break them.
 *
 * @param html - Original HTML or content
 * @param limit - Character limit for the excerpt
 * @returns Formatted and truncated plain text
 */
export const formatExcerpt = (html: string | string[], limit = 150): string => {
  if (!html) return "";

  // Handle case where content is an array of paragraphs
  const content = Array.isArray(html) ? html.join(" ") : html;

  // 1. Strip tags and decode entities BEFORE truncation
  // This ensures that "cùng" truncated to 5 chars becomes "cùng"
  // instead of "c&ugrav" which is broken.
  const plainText = decodeHtmlEntities(stripHtml(content));

  if (plainText.length <= limit) {
    return plainText;
  }

  // 2. Truncate at a word boundary if possible
  let truncated = plainText.substring(0, limit);
  const lastSpace = truncated.lastIndexOf(" ");
  if (lastSpace > limit * 0.7) {
    truncated = truncated.substring(0, lastSpace);
  }

  return truncated.trim() + "...";
};
